import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatient } from '../../hooks';
import { useSaijikiToday, useCreateAiChatConversation } from '../../hooks/api';
import { EmptyState } from '../../components';
import { SaijikiDetailModal } from './components/SaijikiDetailModal';
import {
  Container,
  Header,
  HeaderTitle,
  HeaderSubtext,
  SaijikiCard,
  SaijikiDateLabel,
  SaijikiTitle,
  SaijikiDescription,
  SaijikiReadMore,
  SectionLabel,
  TopicButtonsContainer,
  TopicButton,
  LoadingOverlay,
  LoadingCard,
  LoadingText,
  Spinner,
} from './AiChatTopPage.styled';

const isDateEntry = (dateKey: string) => /^\d{4}$/.test(dateKey);

const formatSaijikiDate = (dateKey: string) => {
  const month = parseInt(dateKey.slice(0, 2), 10);
  const day = parseInt(dateKey.slice(2), 10);
  return `${month}月${day}日`;
};

type Topic = 'daily' | 'health' | 'memory';

const TOPIC_LABELS: Record<Topic, string> = {
  daily: '今日の一日を振り返る',
  health: '健康について話す',
  memory: '思い出を語る',
};

export const AiChatTopPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isSaijikiDetailOpen, setIsSaijikiDetailOpen] = useState(false);
  const createConversation = useCreateAiChatConversation();

  // linkedPatients の取得完了は AppReadyGate で保証済み
  const { linkedPatients } = usePatient();

  const hasSelfRelationship = linkedPatients?.some(p => p.relationship === '本人') ?? false;

  const { data: saijikiData } = useSaijikiToday();

  const handleTopicSelect = useCallback(async (topic: Topic) => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const result = await createConversation.mutateAsync({
        topic,
        conversation_starters: saijikiData?.conversation_starters,
      });
      navigate('/ai-chat/conversation', {
        state: {
          conversationId: result.conversation_id,
          topic: result.topic,
          initialMessage: result.message,
        },
      });
    } catch (error) {
      console.error('[AiChat] Failed to create conversation:', error);
      setIsCreating(false);
    }
  }, [isCreating, createConversation, navigate, saijikiData]);

  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <EmptyState
        fullPage
        title="連携しているご家族が見つかりません"
        description="事業所と連携を行ってください"
        icon="👤"
      />
    );
  }

  return (
    <Container>
      <Header>
        <HeaderTitle>おはなしの部屋</HeaderTitle>
        <HeaderSubtext>いつでもお気軽にどうぞ</HeaderSubtext>
      </Header>

      {!hasSelfRelationship ? (
        <EmptyState
          title="ご利用者本人のみご利用いただけます"
          description="相談室はご利用者ご本人のための機能でございます"
          icon="💬"
        />
      ) : (
        <>
          {saijikiData && (
            <>
              <SaijikiCard onClick={() => setIsSaijikiDetailOpen(true)}>
                {isDateEntry(saijikiData.date_key) && (
                  <SaijikiDateLabel>{formatSaijikiDate(saijikiData.date_key)}</SaijikiDateLabel>
                )}
                <SaijikiTitle>{saijikiData.title}</SaijikiTitle>
                <SaijikiDescription>{saijikiData.description}</SaijikiDescription>
                <SaijikiReadMore>詳しく読む ▸</SaijikiReadMore>
              </SaijikiCard>
              <SaijikiDetailModal
                isOpen={isSaijikiDetailOpen}
                onClose={() => setIsSaijikiDetailOpen(false)}
                saijikiData={saijikiData}
                onStartConversation={() => {
                  setIsSaijikiDetailOpen(false);
                  handleTopicSelect('memory');
                }}
              />
            </>
          )}

          <SectionLabel>本日お話しする内容をお選びください</SectionLabel>

          <TopicButtonsContainer>
            {(Object.entries(TOPIC_LABELS) as [Topic, string][]).map(([topic, label]) => (
              <TopicButton
                key={topic}
                onClick={() => handleTopicSelect(topic)}
                disabled={isCreating}
              >
                {label}
              </TopicButton>
            ))}
          </TopicButtonsContainer>
        </>
      )}

      {isCreating && (
        <LoadingOverlay>
          <LoadingCard>
            <Spinner />
            <LoadingText>話し相手を準備しています...</LoadingText>
          </LoadingCard>
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default AiChatTopPage;
